import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Bem from '@upsales/components/Utils/bemClass';
import { Button, Icon, Text } from '@upsales/components';
import { useAppDispatch, useSelector, useSoftDeployAccess } from 'App/components/hooks';
import { useTranslation } from 'Components/Helpers/translate';
import NavbarDropdown from 'App/navbar/NavbarDropdown';
import type { IntegrationConnectionProblem } from 'App/resources/AllIWant';
import { initFromId, oauth2FieldCount, onRequestOauth2 } from 'Store/reducers/ConfigIntegration';
import EventBus from 'App/services/EventBus';
import logError from 'App/babel/helpers/logError';
import { hasUnsavedChanges } from 'App/components/hooks/useUnsavedChangesGuard';
import { failedSignIn, setSignInFailed, SIGN_IN_FAILED } from './signInFailed';

import './ConnectionProblemPill.scss';
import StateRouter from 'App/services/StateRouter';

// Without `setOauthToNull`: the page marks the broken sign-in and offers its own sign-in, and
// emptying the field would wipe every sign-in of an app that has several (one per Fortnox company).
const openAppSettings = (problem: IntegrationConnectionProblem) =>
	StateRouter.go('administration.integration', {
		id: problem.integrationId,
		configure: problem.level === 'user' ? 'user' : undefined
	});

const ConnectionProblemRow = ({
	problem,
	failed,
	close
}: {
	problem: IntegrationConnectionProblem;
	failed: boolean;
	close: () => void;
}) => {
	const { t } = useTranslation();
	const classes = new Bem('ConnectionProblemPill');
	const account = problem.level === 'account';
	const dispatch = useAppDispatch();
	const openSettings = () => {
		close();
		openAppSettings(problem);
	};
	// The user's own oauth2 sign-in starts right here, and OAuthLanding saves it and comes back
	// (`saveOauth`); anything else is signed in on the app's settings page.
	const signIn = async () => {
		close();
		setSignInFailed(null);
		// Unsaved changes are only guarded on navigations, so the settings page is reached through one.
		// After a failed attempt, the settings page shows why rather than starting the same sign-in again.
		if (!account && !failed && !hasUnsavedChanges()) {
			const state = {
				state: StateRouter.current.name,
				stateParams: StateRouter.params,
				saveOauth: true,
				integrationId: problem.integrationId
			};
			try {
				await dispatch(initFromId(problem.integrationId));
				// A problem names the app, not the sign-in, so with several the settings page is where to pick one.
				if (dispatch(oauth2FieldCount()) === 1 && !hasUnsavedChanges() && dispatch(onRequestOauth2(state))) {
					return;
				}
			} catch (error) {
				logError(error, 'ConnectionProblemPill: could not start the sign-in');
			}
		}
		openAppSettings(problem);
	};

	return (
		<li className={classes.elem('row').mod({ account }).b()}>
			<Icon
				className={classes.elem('rowIcon').b()}
				name={account ? 'exclamation-circle' : 'exclamation-triangle'}
			/>
			<div className={classes.elem('rowText').b()}>
				<Text bold size="sm">
					<button
						className={classes.elem('appLink').b()}
						onClick={openSettings}
						title={t('navbar.connectionProblems.openAppSettings')}
					>
						<span className={classes.elem('appName').b()}>{problem.name}</span>
						<Icon name="external-link" />
					</button>
					{account ? (
						<span className={classes.elem('tag').b()}>{t('navbar.connectionProblems.everyone')}</span>
					) : null}
				</Text>
				<Text size="sm" color="grey-11">
					{t('navbar.connectionProblems.signInExpired', { date: moment(problem.since).format('L') })}
				</Text>
				{failed ? (
					<Text size="sm" color="red" bold className={classes.elem('rowError').b()}>
						{t('navbar.connectionProblems.signInFailedHint')}
					</Text>
				) : null}
			</div>
			<Button size="sm" color="red" onClick={signIn}>
				{t('navbar.connectionProblems.signIn')}
			</Button>
		</li>
	);
};

const ConnectionProblemPill = () => {
	const { t } = useTranslation();
	const classes = new Bem('ConnectionProblemPill');
	const hasFlag = useSoftDeployAccess('INTEGRATION_RECONNECT_PILL');
	const problems = useSelector(({ App }) => App.metadata?.integrations.connectionProblems) ?? [];
	const [compact, setCompact] = useState(false);
	const wrapRef = useRef<HTMLLIElement>(null);
	const measureRef = useRef<HTMLSpanElement>(null);

	// Set by OAuthLanding when a sign-in started here didn't go through; kept until the next attempt,
	// or until the problem is fixed some other way.
	const [failedId, setFailedId] = useState(failedSignIn);
	const failedProblem = problems.find(problem => problem.level === 'user' && problem.integrationId === failedId);
	// Not merely absent: the problems may not have loaded yet after the provider's full page load.
	const sawFailedProblem = useRef(false);

	useEffect(
		() => EventBus.on(SIGN_IN_FAILED, (event, integrationId: number | null) => setFailedId(integrationId)),
		[]
	);

	useEffect(() => {
		if (failedProblem) {
			sawFailedProblem.current = true;
		} else if (sawFailedProblem.current) {
			sawFailedProblem.current = false;
			setSignInFailed(null);
		}
	}, [failedProblem]);

	const label = failedProblem
		? t('navbar.connectionProblems.signInFailed', { name: failedProblem.name })
		: problems.length === 1
		? t('navbar.connectionProblems.signInRequiredOne', { name: problems[0].name })
		: t('navbar.connectionProblems.signInRequiredMany', { count: problems.length });

	const shown = hasFlag && problems.length > 0;

	// The text pill while it fits between the search box and the right-hand navbar items, else ❗ N.
	useEffect(() => {
		if (!shown) {
			return;
		}
		const check = () => {
			const pill = wrapRef.current;
			const text = measureRef.current;
			if (!pill || !text) {
				return;
			}
			const navbar = document.getElementById('navbar');
			// The right-hand items are floats that wrap onto the submenu row when space runs out instead
			// of overlapping, so add up their widths rather than reading where they are drawn.
			const rightWidth = Array.from(navbar?.children ?? [])
				.filter(
					el => !el.contains(pill) && el.id !== 'sub-menu' && getComputedStyle(el).position !== 'absolute'
				)
				.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
			const room =
				(navbar?.clientWidth ?? window.innerWidth) - rightWidth - pill.getBoundingClientRect().left - 24;
			setCompact(text.offsetWidth > room);
		};
		check();
		// The brand dropdown and quota charts render after the startup data arrives.
		const observer = new ResizeObserver(check);
		Array.from(document.getElementById('navbar')?.children ?? []).forEach(el => observer.observe(el));
		window.addEventListener('resize', check);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', check);
		};
	}, [shown, label]);

	if (!shown) {
		return null;
	}

	return (
		<li className={classes.b()} ref={wrapRef}>
			<NavbarDropdown
				align="left"
				className={classes.elem('dropdown').b()}
				renderTrigger={({ open, setOpen }) => (
					<button
						className={classes.elem('pill').mod({ compact }).b()}
						onMouseDown={() => setOpen(!open)}
						aria-label={label}
						aria-expanded={open}
						title={compact ? label : undefined}
					>
						<Icon name="exclamation-circle" color="white" />
						{compact ? (
							<Text size="xs" bold color="white" className={classes.elem('count').b()}>
								{problems.length}
							</Text>
						) : (
							<>
								<Text size="sm" bold color="white">
									{label}
								</Text>
								<Icon name="caret-down" color="white" />
							</>
						)}
					</button>
				)}
			>
				{({ setOpen }) => (
					<>
						<Text bold className={classes.elem('cardHeader').b()}>
							{t('navbar.connectionProblems.cardTitle')}
						</Text>
						<ul className={classes.elem('list').b()}>
							{problems.map(problem => (
								<ConnectionProblemRow
									key={`${problem.level}-${problem.integrationId}`}
									problem={problem}
									failed={problem === failedProblem}
									close={() => setOpen(false)}
								/>
							))}
						</ul>
					</>
				)}
			</NavbarDropdown>
			<span className={classes.elem('pill', classes.elem('measure').b()).b()} ref={measureRef} aria-hidden>
				<Icon name="exclamation-circle" />
				<Text size="sm" bold>
					{label}
				</Text>
				<Icon name="caret-down" />
			</span>
		</li>
	);
};

export default ConnectionProblemPill;
